// src/components/invoices/BudgetQuoteSuccessModal.jsx
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Chip,
    Divider,
    Alert,
    IconButton
} from '@mui/material';
import {
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Print as PrintIcon
} from '@mui/icons-material';

function BudgetQuoteSuccessModal({ 
    open, 
    onClose, 
    quoteData,
    onClearCart, 
    onKeepCart 
}) {
    const [error, setError] = useState('');

    // Format currency
    const formatPrice = useCallback((price) => {
        if (typeof price !== 'number' || isNaN(price)) {
            return 'N/A';
        }
        return `$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }, []);

    // Format date
    const formatDate = useCallback((dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            let date;
            if (dateString instanceof Date) {
                date = dateString;
            } else if (typeof dateString === 'string' && dateString.includes('/')) {
                const [day, month, year] = dateString.split('/');
                date = new Date(year, month - 1, day);
            } else if (typeof dateString === 'string' && dateString.includes('-')) {
                date = new Date(dateString);
            } else {
                date = new Date(dateString);
            }
            
            if (isNaN(date.getTime())) {
                return 'N/A';
            }
            
            return date.toLocaleDateString('es-AR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return 'N/A';
        }
    }, []);

    // Handle print/view presupuesto
    const handlePrint = useCallback(() => {
        try {
            setError('');
            
            // Get quote ID from response
            const quoteId = quote.quoteId || quote.id;
            
            if (!quoteId) {
                setError('No se pudo obtener el ID del presupuesto.');
                return;
            }
            
            // Open print page with budget quote ID as URL parameter
            const printPageUrl = `${window.location.origin}/#/print-quote?budgetQuoteId=${quoteId}`;
            window.open(printPageUrl, '_blank');
            
        } catch (err) {
            console.error('Error opening print page:', err);
            setError(`Error al abrir la página de impresión: ${err.message}`);
        }
    }, [quoteData]);

    // Handle keep cart and close
    const handleKeepCartAndClose = useCallback(() => {
        if (onKeepCart) {
            onKeepCart();
        }
        onClose();
    }, [onKeepCart, onClose]);

    // Handle clear cart and close
    const handleClearCartAndClose = useCallback(() => {
        if (onClearCart) {
            onClearCart();
        }
        onClose();
    }, [onClearCart, onClose]);

    if (!quoteData) {
        return null;
    }

    const quote = quoteData.data || quoteData;
    const totals = quoteData.cartTotals || {};

    return (
        <Dialog
            open={open}
            onClose={handleKeepCartAndClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    maxHeight: '95vh',
                    minHeight: '600px',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <DialogTitle sx={{ 
                backgroundColor: '#47ba8c', 
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon fontSize="large" />
                    <Typography variant="h5" component="div">
                        ¡Presupuesto Generado Exitosamente!
                    </Typography>
                </Box>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleKeepCartAndClose}
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Status */}
                <Box sx={{ display: 'center', mb: 2 }}>
                    <Chip
                        icon={<CheckCircleIcon />}
                        label="Estado: Guardado"
                        color="success"
                        variant="outlined"
                        size="medium"
                    />
                </Box>

                {/* Quote Details Grid */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                    mb: 2
                }}>
                    {/* Left Column */}
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary" sx={{ mb: 1 }}>
                            Datos del Presupuesto
                        </Typography>
                        
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Número de Presupuesto
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {quote.quoteNumber || 'N/A'}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Referencia Externa
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {quote.externalReference || 'N/A'}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Fecha de Emisión
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatDate(new Date())}
                            </Typography>
                        </Box>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Fecha de Vencimiento
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatDate(quote.expirationDate) || formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right Column */}
                    <Box>
                        <Typography variant="h6" gutterBottom color="primary">
                            Información Adicional
                        </Typography>
                        
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>Este presupuesto NO se registró en AFIP</strong> y no afectará 
                                las estadísticas de ventas. Puede convertirlo a factura desde la 
                                página de Presupuestos.
                            </Typography>
                        </Alert>

                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                Validez
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                30 días
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Financial Summary */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                        Resumen Financiero
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                        gap: 2
                    }}>
                        <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Subtotal
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                                {formatPrice(totals.subtotal || 0)}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Descuento
                            </Typography>
                            <Typography variant="h6" color="warning.main">
                                -{formatPrice(totals.discount || 0)}
                            </Typography>
                        </Box>

                        <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                Total
                            </Typography>
                            <Typography variant="h5" color="success.main" fontWeight="bold">
                                {formatPrice(totals.total || 0)}
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    justifyContent: 'center',
                    mb: 2
                }}>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        sx={{ 
                            backgroundColor: '#4775ba',
                            '&:hover': { backgroundColor: '#3a5d94' }
                        }}
                    >
                        Imprimir/Guardar Presupuesto
                    </Button>
                </Box>

                {/* Navigation tip */}
                <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                        Puede ver y gestionar todos sus presupuestos desde el menú: 
                        <strong> Ventas → Presupuestos</strong>
                    </Typography>
                </Alert>
            </DialogContent>

            <DialogActions sx={{ p: 3, backgroundColor: '#f9f9f9' }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    width: '100%',
                    justifyContent: 'space-between'
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleKeepCartAndClose}
                        sx={{ flex: 1 }}
                    >
                        Mantener Carrito
                    </Button>
                    
                    <Button
                        variant="contained"
                        onClick={handleClearCartAndClose}
                        sx={{ 
                            flex: 1,
                            backgroundColor: '#f5d540',
                            color: 'black',
                            '&:hover': { backgroundColor: '#e0c030' }
                        }}
                    >
                        Limpiar Carrito
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
}

BudgetQuoteSuccessModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    quoteData: PropTypes.shape({
        data: PropTypes.shape({
            quoteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            quoteNumber: PropTypes.string,
            externalReference: PropTypes.string,
            expirationDate: PropTypes.string,
        }),
    }),
    onClearCart: PropTypes.func,
    onKeepCart: PropTypes.func,
};

export default BudgetQuoteSuccessModal;
